import { useEffect, useState } from 'react';
import { getStoredAttribution } from '@api/attribution';
import { resolveCampaignReferral } from '@api/campaign-resolve';

/**
 * Desktop installer (direct .exe download) and the mobile app store landing
 * page. Both carry UTM params provided by the marketing team so attribution
 * lines up across surfaces. If we need to swap campaigns later, both links
 * live here.
 *
 * The base URLs omit `utm_campaign`; it is appended last so a visitor who
 * arrived via a campaign `?ref=` overrides the default with their own campaign
 * (see useDownloadTarget). The defaults are the house Overwolf affiliation ids
 * used for organic / un-attributed downloads.
 */
const DESKTOP_INSTALLER_BASE =
  'https://download.overwolf.com/install/Download?ExtensionId=mgcdimjpffpbkbcmlbgonidnbagepcokjoeaemcb&utm_source=app&utm_medium=owaa';
const DEFAULT_DESKTOP_UTM_CAMPAIGN = '8QaPlnP';

const MOBILE_APP_STORE_BASE =
  'https://www.overwolf.com/app/mainstage_labs-mainstage?utm_source=app&utm_medium=owaa';
const DEFAULT_MOBILE_UTM_CAMPAIGN = 'f3orEe6';

function buildInstallUrl(base: string, utmCampaign: string): string {
  return `${base}&utm_campaign=${encodeURIComponent(utmCampaign)}`;
}

/**
 * Detect a mobile OS via user-agent. We're routing the *download* link
 * (Windows installer vs. mobile app-store page), so OS — not viewport
 * width — is what we want here. A user on a phone in landscape still
 * needs the store URL; a user resizing their browser window narrow on
 * Windows still needs the installer.
 */
function detectIsMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Modern iPads report as Macintosh + touch — catch them too so iOS users
  // never hit the Windows installer link.
  const iPadOS =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  return /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(ua) || iPadOS;
}

export type DownloadTarget = {
  href: string;
  isMobile: boolean;
  label: string;
};

/**
 * Returns the right "Download App" destination for the current device.
 * - Desktop → direct Windows installer (the companion is a Windows-only
 *   Overwolf app, so this is the fastest path to installed).
 * - Mobile → the app store landing page so the visitor can come back from
 *   a real computer.
 *
 * We start with a desktop assumption on the very first render so SSR /
 * static pre-render and the no-JS preview pick the installer link, then
 * re-check on mount once `navigator` is available.
 */
export function useDownloadTarget(): DownloadTarget {
  const [isMobile, setIsMobile] = useState(false);
  // The Overwolf affiliation id to stamp on the install when the visitor
  // arrived via a campaign link. Null until resolved (or when there's no ref),
  // in which case the house default is used.
  const [campaignUtm, setCampaignUtm] = useState<string | null>(null);

  useEffect(() => {
    setIsMobile(detectIsMobile());
  }, []);

  useEffect(() => {
    // If the visitor landed via a campaign `?ref=`, attribute the install to
    // the SAME campaign so the webapp → download → install → in-app-signup
    // chain isn't lost. Prefer the campaign's registered Overwolf id (keeps
    // Overwolf-side affiliate credit); fall back to the ref code, which the
    // backend still matches on the campaign `code`. Best-effort: on any
    // failure we leave the house default in place.
    const ref = getStoredAttribution()?.referralCode;
    if (!ref) return;

    let cancelled = false;
    resolveCampaignReferral(ref).then((resolved) => {
      if (cancelled || !resolved) return;
      setCampaignUtm(resolved.overwolfCampaignId ?? resolved.code);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const base = isMobile ? MOBILE_APP_STORE_BASE : DESKTOP_INSTALLER_BASE;
  const defaultUtm = isMobile
    ? DEFAULT_MOBILE_UTM_CAMPAIGN
    : DEFAULT_DESKTOP_UTM_CAMPAIGN;

  return {
    href: buildInstallUrl(base, campaignUtm ?? defaultUtm),
    isMobile,
    label: isMobile ? 'Get the App' : 'Download Desktop App',
  };
}
